import { AlertTriangle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  onConfirm: () => void | Promise<void>;
  icon?: React.ReactNode | null;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  loadingLabel,
  onConfirm,
  icon,
}: ConfirmDialogProps) {
  const showIcon = icon !== null;
  const renderedIcon =
    icon ??
    (destructive ? (
      <AlertTriangle className="h-6 w-6 text-destructive" />
    ) : null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading) {
          return;
        }

        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          {showIcon && renderedIcon && (
            <div
              className={
                "mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full " +
                (destructive ? "bg-destructive/10" : "bg-primary/10")
              }
            >
              {renderedIcon}
            </div>
          )}
          <DialogTitle className={showIcon ? "text-center" : undefined}>
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription
              className={showIcon ? "text-center" : undefined}
            >
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={() => onConfirm()}
            disabled={loading}
          >
            {loading ? loadingLabel ?? "Working..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
